import {useState, useEffect, useRef, useMemo} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Badge} from "@/components/ui/badge";
import {Switch} from "@/components/ui/switch";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import {Progress} from "@/components/ui/progress";
import {useToast} from "@/hooks/use-toast";
import {queryClient} from "@/lib/queryClient";
import {youtubeService} from "@/services/youtubeService";
import {sourcesService} from "@/services/sourcesService";
import {knowledgeBaseService} from "@/services/knowledgeBaseService";
import {
    youtubeSubscriptionService,
    type YouTubeSubscription as DBSubscription,
    type ImportJob as DBImportJob
} from "@/services/youtubeSubscriptionService";
import {
    Youtube,
    Search,
    RefreshCw,
    Trash2,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    Plus,
    ExternalLink,
    Play,
    Users,
    List,
    ArrowUp,
    ArrowDown,
    LayoutGrid,
    Info,
    FileText
} from "lucide-react";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";

// Quick import: single endpoint (no /transcript or /playlist path). TRANSCRIPT_API_KEY in .env / Vercel.
const TRANSCRIPT_API_ENDPOINT = "/api/youtube/transcript-api";
const YOUTUBE_DATA_API_BASE = "https://www.googleapis.com/youtube/v3";
// SECURITY: env-only — no hardcoded key fallback.
const YOUTUBE_DATA_API_KEY = import.meta.env.VITE_YOUTUBE_DATA_API_KEY || "";
import {useMastermind} from "@/contexts/MastermindContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {SelectionToolbar} from "@/components/SelectionToolbar";
import DOMPurify from "dompurify";

// Map database fields to component interface
const mapSubscription = (db: DBSubscription): YouTubeSubscription => ({
    id: db.id,
    channelId: db.channel_id,
    channelName: db.channel_name,
    channelUrl: db.channel_url,
    channelThumbnail: db.channel_thumbnail,
    subscriberCount: db.subscriber_count,
    syncFrequency: db.sync_frequency,
    autoImport: db.auto_import,
    aiEnhance: db.ai_enhance,
    isActive: db.is_active,
    lastSyncedAt: db.last_synced_at,
    createdAt: db.created_at,
});

/** Canonical YouTube URL for a video ID (for duplicate matching). */
function canonicalYouTubeUrl(videoId: string): string {
    return `https://www.youtube.com/watch?v=${videoId}`;
}

/** Extract YouTube video ID from doc content (e.g. "Video URL: ..." or "Video ID: ..." in body). */
function extractVideoIdFromContent(content: string | null | undefined): string | null {
    if (!content || typeof content !== "string") return null;
    const fromUrl = youtubeService.extractVideoId(content);
    if (fromUrl) return fromUrl;
    const videoIdMatch = content.match(/(?:Video ID:\s*|video_id[=:]\s*)([a-zA-Z0-9_-]{11})/i);
    return videoIdMatch ? videoIdMatch[1] : null;
}

/** Build sets of existing YouTube URLs and titles from sources for duplicate check. */
function buildDuplicateSets(sources: { type?: string; url?: string | null; title?: string | null }[]): {
    existingUrls: Set<string>;
    existingTitles: Set<string>;
} {
    const existingUrls = new Set<string>();
    const existingTitles = new Set<string>();
    for (const s of sources) {
        if (s.type !== "youtube") continue;
        const id = s.url ? youtubeService.extractVideoId(s.url) : null;
        if (id) existingUrls.add(canonicalYouTubeUrl(id));
        if (s.title?.trim()) existingTitles.add(s.title.trim().toLowerCase());
    }
    return { existingUrls, existingTitles };
}

const mapImportJob = (db: DBImportJob): ImportJob => ({
    id: db.id,
    sourceType: db.source_type,
    externalId: db.external_id,
    title: db.title,
    status: db.status,
    progress: db.progress,
    progressMessage: db.progress_message,
    errorMessage: db.error_message,
    startedAt: db.started_at,
    completedAt: db.completed_at,
    createdAt: db.created_at,
});

interface YouTubeSubscription {
    id: string;
    channelId: string;
    channelName: string;
    channelUrl: string | null;
    channelThumbnail: string | null;
    subscriberCount: string | null;
    syncFrequency: string;
    autoImport: boolean;
    aiEnhance: boolean;
    isActive: boolean;
    lastSyncedAt: string | null;
    createdAt: string;
}

interface ImportJob {
    id: string;
    sourceType: string;
    externalId: string | null;
    title: string | null;
    status: string;
    progress: string | null;
    progressMessage: string | null;
    errorMessage: string | null;
    startedAt: string | null;
    completedAt: string | null;
    createdAt: string;
}

interface ChannelSearchResult {
    channelId: string;
    channelName: string;
    channelUrl: string;
    channelThumbnail: string;
    subscriberCount: string;
    description: string;
}

interface ChannelVideoBrowserItem {
    videoId: string;
    title: string;
    publishedAt: string;
    thumbnail: string;
    url: string;
    transcript?: string;
    isFetchingTranscript?: boolean;
}

interface ChannelPlaylistBrowserItem {
    playlistId: string;
    title: string;
    publishedAt: string;
    itemCount: number;
    thumbnail: string;
    url: string;
}

interface PlaylistTranscriptSummary {
    isFetching: boolean;
    videos: Array<{ videoId: string; title: string; transcript: string }>;
}

export default function YouTubeAutomation() {
    const {toast} = useToast();
    const {clipToSidebar, cancelDocumentEdit} = useMastermind();
    const {currentWorkspaceId} = useWorkspace();
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<ChannelSearchResult[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchResultsAccordionValue, setSearchResultsAccordionValue] = useState<string | undefined>(undefined);
    const [isSearching, setIsSearching] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");
    const [playlistUrl, setPlaylistUrl] = useState("");
    const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState<ChannelSearchResult | null>(null);
    const [subscribeSettings, setSubscribeSettings] = useState({
        syncFrequency: "daily",
        autoImport: true,
        aiEnhance: true
    });
    const [playlistProgress, setPlaylistProgress] = useState<{
        current: number;
        total: number;
        videoId: string
    } | null>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const [videoTranscripts, setVideoTranscripts] = useState<Array<{ transcript: string; videoId: string; title: string; channelName?: string; thumbnailUrl?: string }>>([]);
    const [videoDuplicatesSkipped, setVideoDuplicatesSkipped] = useState<Array<{ videoId: string; title: string; url: string; reason: "url" | "title" }>>([]);
    const [playlistTranscripts, setPlaylistTranscripts] = useState<any>(null);
    const [isFetchingVideo, setIsFetchingVideo] = useState(false);
    const [isFetchingPlaylist, setIsFetchingPlaylist] = useState(false);
    const [activeTab, setActiveTab] = useState<"subscriptions" | "import" | "jobs">("import");
    const [quickImportSubTab, setQuickImportSubTab] = useState<"video" | "playlist">("video");
    const [subscriptionSortField, setSubscriptionSortField] = useState<"name" | "lastSynced" | "subscribers">("name");
    const [subscriptionSortDirection, setSubscriptionSortDirection] = useState<"asc" | "desc">("asc");
    const [jobSortField, setJobSortField] = useState<"title" | "status" | "createdAt">("createdAt");
    const [jobSortDirection, setJobSortDirection] = useState<"asc" | "desc">("desc");
    const [subscriptionSearchQuery, setSubscriptionSearchQuery] = useState("");
    const [jobSearchQuery, setJobSearchQuery] = useState("");
    const [subscriptionViewMode, setSubscriptionViewMode] = useState<"grid" | "list">("list");
    const [jobViewMode, setJobViewMode] = useState<"grid" | "list">("list");
    const [channelVideosBySubscription, setChannelVideosBySubscription] = useState<Record<string, ChannelVideoBrowserItem[]>>({});
    const [channelPlaylistsBySubscription, setChannelPlaylistsBySubscription] = useState<Record<string, ChannelPlaylistBrowserItem[]>>({});
    const [loadingChannelVideos, setLoadingChannelVideos] = useState<Record<string, boolean>>({});
    const [loadingChannelPlaylists, setLoadingChannelPlaylists] = useState<Record<string, boolean>>({});
    const [playlistTranscriptState, setPlaylistTranscriptState] = useState<Record<string, PlaylistTranscriptSummary>>({});
    const [browserOpen, setBrowserOpen] = useState(false);
    const [browserMode, setBrowserMode] = useState<"videos" | "playlists">("videos");
    const [browserSubscription, setBrowserSubscription] = useState<YouTubeSubscription | null>(null);
    const [browserSearchQuery, setBrowserSearchQuery] = useState("");
    const [browserSortDirection, setBrowserSortDirection] = useState<"asc" | "desc">("asc");
    const [browserViewMode, setBrowserViewMode] = useState<"grid" | "list">("list");

    const {data: subscriptions = [], isLoading: loadingSubscriptions} = useQuery<YouTubeSubscription[]>({
        queryKey: ["youtube-subscriptions"],
        queryFn: async () => {
            const data = await youtubeSubscriptionService.getSubscriptions();
            return data.map(mapSubscription);
        },
        refetchInterval: isPolling ? 30000 : false, // Poll every 30 seconds when enabled
    });

    const {data: importJobs = [], isLoading: loadingJobs} = useQuery<ImportJob[]>({
        queryKey: ["youtube-import-jobs"],
        queryFn: async () => {
            const data = await youtubeSubscriptionService.getImportJobs();
            return data.map(mapImportJob);
        },
        refetchInterval: isPolling ? 10000 : false, // Poll jobs more frequently
    });

    const {data: existingSources = []} = useQuery({
        queryKey: ["/api/sources", currentWorkspaceId ?? null],
        queryFn: () => sourcesService.getSources(undefined, currentWorkspaceId ?? null),
        staleTime: 60_000,
    });

    const {data: knowledgeBaseItems = []} = useQuery({
        queryKey: ["/api/knowledgebase", currentWorkspaceId ?? null],
        queryFn: () => knowledgeBaseService.getKnowledgeBaseItems(undefined, currentWorkspaceId ?? null),
        staleTime: 60_000,
    });

    const youtubeDocs = useMemo(
        () => (knowledgeBaseItems || []).filter((doc) => doc.type === "youtube"),
        [knowledgeBaseItems]
    );
    const filteredAndSortedYouTubeDocs = useMemo(() => {
        let list = [...youtubeDocs];
        if (jobSearchQuery?.trim()) {
            const q = jobSearchQuery.toLowerCase().trim();
            list = list.filter((doc) => {
                const fromContent = extractVideoIdFromContent(doc.content);
                const resolvedUrl = doc.videoUrl || (fromContent ? canonicalYouTubeUrl(fromContent) : "");
                return (
                    (doc.title || "").toLowerCase().includes(q) ||
                    (doc.videoUrl || "").toLowerCase().includes(q) ||
                    (doc.content || "").toLowerCase().includes(q) ||
                    (resolvedUrl || "").toLowerCase().includes(q)
                );
            });
        }
        list.sort((a, b) => {
            let comparison = 0;
            if (jobSortField === "title") {
                comparison = (a.title || "").localeCompare(b.title || "");
            } else {
                const tA = new Date(a.createdAt || 0).getTime();
                const tB = new Date(b.createdAt || 0).getTime();
                comparison = tA - tB;
            }
            return jobSortDirection === "asc" ? comparison : -comparison;
        });
        return list;
    }, [youtubeDocs, jobSortField, jobSortDirection, jobSearchQuery]);

    const transcribedVideoIds = useMemo(() => {
        const ids = new Set<string>();
        for (const doc of youtubeDocs) {
            const fromUrl = doc.videoUrl ? youtubeService.extractVideoId(doc.videoUrl) : null;
            const fromContent = extractVideoIdFromContent(doc.content);
            const id = fromUrl || fromContent;
            if (id) ids.add(id);
        }
        return ids;
    }, [youtubeDocs]);

    const browserVideos = useMemo(() => {
        if (!browserSubscription) return [];
        const items = channelVideosBySubscription[browserSubscription.id] || [];
        const q = browserSearchQuery.trim().toLowerCase();
        const filtered = q
            ? items.filter((item) =>
                (item.title || "").toLowerCase().includes(q) ||
                item.videoId.toLowerCase().includes(q)
            )
            : items;
        return [...filtered].sort((a, b) =>
            browserSortDirection === "asc"
                ? (a.title || "").localeCompare(b.title || "")
                : (b.title || "").localeCompare(a.title || "")
        );
    }, [browserSubscription, channelVideosBySubscription, browserSearchQuery, browserSortDirection]);

    const browserPlaylists = useMemo(() => {
        if (!browserSubscription) return [];
        const items = channelPlaylistsBySubscription[browserSubscription.id] || [];
        const q = browserSearchQuery.trim().toLowerCase();
        const filtered = q
            ? items.filter((item) =>
                (item.title || "").toLowerCase().includes(q) ||
                item.playlistId.toLowerCase().includes(q)
            )
            : items;
        return [...filtered].sort((a, b) =>
            browserSortDirection === "asc"
                ? (a.title || "").localeCompare(b.title || "")
                : (b.title || "").localeCompare(a.title || "")
        );
    }, [browserSubscription, channelPlaylistsBySubscription, browserSearchQuery, browserSortDirection]);

    // Auto-poll active subscriptions for new videos
    useEffect(() => {
        const activeSubscriptions = subscriptions.filter(sub => sub.isActive && sub.autoImport);

        if (activeSubscriptions.length > 0) {
            setIsPolling(true);

            // Sync all active subscriptions periodically based on their frequency
            const syncAllSubscriptions = async () => {
                for (const sub of activeSubscriptions) {
                    const shouldSync = shouldSyncSubscription(sub);
                    if (shouldSync) {
                        try {
                            const data = await youtubeSubscriptionService.syncSubscription(sub.id);
                            if (data.newVideos > 0) {
                                toast({
                                    title: "New videos detected",
                                    description: `${data.newVideos} new video(s) from ${sub.channelName} have been imported and transcribed.`
                                });
                                queryClient.invalidateQueries({queryKey: ["youtube-subscriptions"]});
                                queryClient.invalidateQueries({queryKey: ["youtube-import-jobs"]});
                                queryClient.invalidateQueries({queryKey: ["/api/sources"]});
                            }
                        } catch (error) {
                            console.error(`Failed to sync subscription ${sub.id}:`, error);
                        }
                    }
                }
            };

            // Initial sync check
            syncAllSubscriptions();

            // Set up interval based on the most frequent sync frequency
            const intervals: Record<string, number> = {
                hourly: 60 * 60 * 1000,
                daily: 24 * 60 * 60 * 1000,
                weekly: 7 * 24 * 60 * 60 * 1000,
                manual: Infinity
            };

            const minInterval = Math.min(
                ...activeSubscriptions.map(sub => intervals[sub.syncFrequency] || Infinity)
            );

            if (minInterval !== Infinity) {
                pollingIntervalRef.current = setInterval(syncAllSubscriptions, Math.max(minInterval, 60000)); // At least 1 minute
            }

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                setIsPolling(false);
            };
        } else {
            setIsPolling(false);
        }
    }, [subscriptions, toast]);

    const shouldSyncSubscription = (sub: YouTubeSubscription): boolean => {
        if (!sub.lastSyncedAt) return true;

        const lastSync = new Date(sub.lastSyncedAt).getTime();
        const now = Date.now();
        const intervals: Record<string, number> = {
            hourly: 60 * 60 * 1000,
            daily: 24 * 60 * 60 * 1000,
            weekly: 7 * 24 * 60 * 60 * 1000,
            manual: Infinity
        };

        const interval = intervals[sub.syncFrequency] || Infinity;
        return (now - lastSync) >= interval;
    };

    const searchChannelsMutation = useMutation({
        mutationFn: async (query: string) => {
            // Note: This requires YouTube Data API implementation
            // For now, return empty array - you'll need to implement channel search
            const results = await youtubeSubscriptionService.searchChannels(query);
            return results;
        },
        onSuccess: (data) => {
            setSearchResults(data);
            if (data.length > 0) {
                setShowSearchResults(true);
                setSearchResultsAccordionValue("channel-search-results");
            } else {
                setShowSearchResults(false);
                setSearchResultsAccordionValue(undefined);
            }
            setIsSearching(false);
        },
        onError: (error: Error) => {
            toast({title: "Search failed", description: error.message, variant: "destructive"});
            setIsSearching(false);
        }
    });

    const subscribeMutation = useMutation({
        mutationFn: async (channelData: any) => {
            return youtubeSubscriptionService.createSubscription(channelData);
        },
        onSuccess: () => {
            toast({title: "Subscribed", description: "Channel added to your subscriptions"});
            queryClient.invalidateQueries({queryKey: ["youtube-subscriptions"]});
            setShowSubscribeDialog(false);
            setSelectedChannel(null);
            // Collapse search results after successful subscribe; user can reopen if needed.
            setSearchResultsAccordionValue(undefined);
        },
        onError: (error: Error) => {
            toast({title: "Subscribe failed", description: error.message, variant: "destructive"});
        }
    });

    const unsubscribeMutation = useMutation({
        mutationFn: async (id: string) => {
            return youtubeSubscriptionService.deleteSubscription(id);
        },
        onSuccess: () => {
            toast({title: "Unsubscribed", description: "Channel removed from subscriptions"});
            queryClient.invalidateQueries({queryKey: ["youtube-subscriptions"]});
        },
        onError: (error: Error) => {
            toast({title: "Unsubscribe failed", description: error.message, variant: "destructive"});
        }
    });

    const syncMutation = useMutation({
        mutationFn: async (id: string) => {
            return youtubeSubscriptionService.syncSubscription(id);
        },
        onSuccess: (data: any) => {
            toast({
                title: "Sync complete",
                description: `Imported ${data.newVideos} new videos`
            });
            queryClient.invalidateQueries({queryKey: ["youtube-subscriptions"]});
            queryClient.invalidateQueries({queryKey: ["youtube-import-jobs"]});
        },
        onError: (error: Error) => {
            toast({title: "Sync failed", description: error.message, variant: "destructive"});
        }
    });

    const importVideoMutation = useMutation({
        mutationFn: async (url: string) => {
            return youtubeService.importVideo(url, currentWorkspaceId ?? null);
        },
        onSuccess: (data) => {
            if (data.success) {
                toast({
                    title: "Video imported",
                    description: `"${data.title}" has been transcribed and added to your sources`
                });
                setVideoUrl("");
                queryClient.invalidateQueries({queryKey: ["/api/sources"]});
                queryClient.invalidateQueries({queryKey: ["youtube-import-jobs"]});
            } else {
                toast({
                    title: "Import failed",
                    description: data.error || "Failed to import video",
                    variant: "destructive"
                });
            }
        },
        onError: (error: Error) => {
            toast({title: "Import failed", description: error.message, variant: "destructive"});
        }
    });

    const fetchVideoTranscript = async () => {
        const lines = videoUrl.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
        // Preserve order and duplicates so we can flag duplicate URLs/titles within the pasted list
        const videoIdList = lines.map((line) => youtubeService.extractVideoId(line)).filter((id): id is string => Boolean(id));
        const uniqueCount = new Set(videoIdList).size;
        console.log('[Quick Import] fetchVideoTranscript called', { lineCount: videoIdList.length, uniqueVideoIds: uniqueCount, videoIdList });
        if (videoIdList.length === 0) {
            toast({
                title: "Invalid URL(s)",
                description: "Please enter one or more valid YouTube video URLs (one per line).",
                variant: "destructive"
            });
            return;
        }
        setIsFetchingVideo(true);
        setVideoTranscripts([]);
        setVideoDuplicatesSkipped([]);

        const sources = existingSources;
        const { existingUrls, existingTitles } = buildDuplicateSets(sources);
        const seenUrlsInBatch = new Set<string>();
        const seenTitlesInBatch = new Set<string>();

        const results: Array<{ transcript: string; videoId: string; title: string; channelName?: string; thumbnailUrl?: string }> = [];
        const skippedDuplicates: Array<{ videoId: string; title: string; url: string; reason: "url" | "title" }> = [];
        let failed = 0;

        for (const videoId of videoIdList) {
            const url = canonicalYouTubeUrl(videoId);
            let metadata = { title: 'YouTube Video', description: '', channelName: 'Unknown', publishedAt: '', thumbnailUrl: '' };
            try {
                metadata = await youtubeService.fetchVideoMetadata(videoId);
            } catch (err) {
                console.error('Failed to fetch video metadata:', err);
            }
            const rawTitle = metadata.title && metadata.title !== 'YouTube Video' ? metadata.title : `Video ${videoId}`;
            const title = rawTitle || `Video ${videoId}`;
            const titleLower = title.trim().toLowerCase();

            const duplicateByUrl = existingUrls.has(url) || seenUrlsInBatch.has(url);
            const duplicateByTitle = titleLower && (existingTitles.has(titleLower) || seenTitlesInBatch.has(titleLower));
            if (duplicateByUrl) {
                skippedDuplicates.push({ videoId, title, url, reason: "url" });
                seenUrlsInBatch.add(url);
                if (titleLower) seenTitlesInBatch.add(titleLower);
                continue;
            }
            if (duplicateByTitle) {
                skippedDuplicates.push({ videoId, title, url, reason: "title" });
                seenUrlsInBatch.add(url);
                if (titleLower) seenTitlesInBatch.add(titleLower);
                continue;
            }
            seenUrlsInBatch.add(url);
            if (titleLower) seenTitlesInBatch.add(titleLower);

            try {
                const transcriptUrl = TRANSCRIPT_API_ENDPOINT + "?video_id=" + encodeURIComponent(videoId);
                console.log('[Quick Import] request URL (must be /api/youtube/transcript-api?video_id=..., no /transcript in path):', transcriptUrl);
                const res = await fetch(transcriptUrl);
                const data = await res.json().catch(() => ({}));
                console.log('[Quick Import] transcript API response', { ok: res.ok, status: res.status, data, transcriptLength: data?.transcript?.length });
                if (!res.ok) {
                    let message = res.statusText || 'Failed to fetch transcript';
                    if (data?.error && typeof data.error === 'string') message = data.error;
                    if (data?.detail && typeof data.detail === 'string') message = data.detail;
                    if (res.status >= 500) {
                        message = `Transcript service error (${res.status}). This video may not have captions, or the service may be temporarily unavailable.`;
                    }
                    throw new Error(message);
                }
                const transcriptArray = data?.transcript || [];
                const transcriptText = Array.isArray(transcriptArray)
                    ? transcriptArray.map((item: { text?: string }) => item?.text ?? '').filter(Boolean).join('\n')
                    : '';
                const finalTitle = (data.title && data.title !== 'YouTube Video' ? data.title : title) || title;
                results.push({
                    transcript: transcriptText || '(No transcript text)',
                    videoId,
                    title: finalTitle,
                    channelName: metadata.channelName,
                    thumbnailUrl: metadata.thumbnailUrl
                });
            } catch (error: any) {
                failed++;
                toast({
                    title: `Failed: ${metadata.title || videoId}`,
                    description: error?.message || "Failed to fetch transcript",
                    variant: "destructive"
                });
            }
        }

        setVideoTranscripts(results);
        setVideoDuplicatesSkipped(skippedDuplicates);
        // Always log skip list for confirmation (duplicates not fetched — URL or title already in library or in this list)
        console.log('[Quick Import] Duplicates skipped (not fetched):', {
            count: skippedDuplicates.length,
            list: skippedDuplicates.map((d) => ({ videoId: d.videoId, title: d.title, reason: d.reason }))
        });
        if (results.length > 0 || skippedDuplicates.length > 0) {
            const parts: string[] = [];
            if (results.length > 0) parts.push(`Fetched ${results.length} video(s)`);
            if (failed > 0) parts.push(`${failed} failed`);
            if (skippedDuplicates.length > 0) parts.push(`${skippedDuplicates.length} skipped (duplicate)`);
            toast({
                title: "Quick Import",
                description: parts.join(". ") || `Processed ${videoIdList.length} video(s).`
            });
        }
        setIsFetchingVideo(false);
    };

    const fetchPlaylistTranscripts = async () => {
        const playlistId = youtubeService.extractPlaylistId(playlistUrl);
        if (!playlistId) {
            toast({
                title: "Invalid URL",
                description: "Please enter a valid YouTube playlist URL",
                variant: "destructive"
            });
            return;
        }
        setIsFetchingPlaylist(true);
        setPlaylistTranscripts(null);
        try {
            const res = await fetch(TRANSCRIPT_API_ENDPOINT + "?playlist_id=" + encodeURIComponent(playlistId));
            const data = await res.json().catch(() => ({}));
            console.log('[Quick Import] playlist API response', { ok: res.ok, status: res.status, playlistId: data?.playlistId, total: data?.total, videosCount: data?.videos?.length, data });
            if (!res.ok) {
                let message = res.statusText || 'Failed to fetch playlist';
                if (data?.error && typeof data.error === 'string') message = data.error;
                if (data?.detail && typeof data.detail === 'string') message = data.detail;
                throw new Error(message);
            }
            const rawVideos = data.videos || [];
            const sources = existingSources;
            const { existingUrls, existingTitles } = buildDuplicateSets(sources);
            const seenUrlInPlaylist = new Set<string>();
            const seenTitleInPlaylist = new Set<string>();
            const videos = rawVideos.map((v: any) => {
                const videoId = v.videoId ?? v.video_id ?? '';
                const title = (v.title && v.title !== 'YouTube Video' ? v.title : `Video ${videoId}`) || `Video ${videoId}`;
                const url = canonicalYouTubeUrl(videoId);
                const titleLower = title.trim().toLowerCase();
                const duplicateByUrl = existingUrls.has(url) || seenUrlInPlaylist.has(url);
                const duplicateByTitle = titleLower && (existingTitles.has(titleLower) || seenTitleInPlaylist.has(titleLower));
                const duplicate = duplicateByUrl || duplicateByTitle;
                const duplicateReason = duplicateByUrl ? 'url' as const : 'title' as const;
                seenUrlInPlaylist.add(url);
                if (titleLower) seenTitleInPlaylist.add(titleLower);
                return { ...v, videoId, title, duplicate: !!duplicate, duplicateReason: duplicate ? duplicateReason : undefined };
            });
            setPlaylistTranscripts({
                playlistId: data.playlistId || playlistId,
                total: data.total ?? rawVideos.length,
                videos
            });
            const successCount = videos.filter((v: any) => v.transcript && v.transcript.length > 0).length;
            const duplicateCount = videos.filter((v: any) => v.duplicate).length;
            const playlistDuplicateList = videos.filter((v: any) => v.duplicate).map((v: any) => ({ videoId: v.videoId, title: v.title, reason: v.duplicateReason }));
            // Always log duplicate/skip list for confirmation
            console.log('[Quick Import] Playlist duplicates skipped (not fetched / flagged):', { count: playlistDuplicateList.length, list: playlistDuplicateList });
            const desc = duplicateCount > 0
                ? `Fetched ${successCount} of ${rawVideos.length} videos. ${duplicateCount} flagged as duplicate (already in library or repeated in playlist).`
                : `Successfully fetched transcripts for ${successCount} of ${data.total ?? rawVideos.length} videos`;
            toast({
                title: "Playlist transcripts fetched",
                description: desc
            });
        } catch (error: any) {
            toast({
                title: "Failed to fetch playlist",
                description: error?.message || "An unknown error occurred",
                variant: "destructive"
            });
        } finally {
            setIsFetchingPlaylist(false);
        }
    };

    const importPlaylistMutation = useMutation({
        mutationFn: async (url: string) => {
            return youtubeService.importPlaylist(url, (current, total, videoId) => {
                setPlaylistProgress({current, total, videoId});
            }, currentWorkspaceId ?? null);
        },
        onSuccess: (data) => {
            setPlaylistProgress(null);
            if (data.success) {
                toast({
                    title: "Playlist imported",
                    description: `Successfully imported ${data.imported} video(s). ${data.failed > 0 ? `${data.failed} failed.` : ""}`
                });
                setPlaylistUrl("");
                queryClient.invalidateQueries({queryKey: ["/api/sources"]});
                queryClient.invalidateQueries({queryKey: ["youtube-import-jobs"]});
            } else {
                toast({
                    title: "Playlist import failed",
                    description: data.errors?.join(", ") || "Failed to import playlist",
                    variant: "destructive"
                });
            }
        },
        onError: (error: Error) => {
            setPlaylistProgress(null);
            toast({title: "Playlist import failed", description: error.message, variant: "destructive"});
        }
    });

    const handleSearch = () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        searchChannelsMutation.mutate(searchQuery);
    };

    const handleCloseSearchResults = () => {
        setShowSearchResults(false);
        setSearchResults([]);
        setSearchResultsAccordionValue(undefined);
    };

    const fetchChannelVideosForSubscription = async (sub: YouTubeSubscription) => {
        setLoadingChannelVideos((prev) => ({ ...prev, [sub.id]: true }));
        try {
            const url = `${YOUTUBE_DATA_API_BASE}/search?part=snippet&channelId=${encodeURIComponent(sub.channelId)}&type=video&order=date&maxResults=20&key=${encodeURIComponent(YOUTUBE_DATA_API_KEY)}`;
            const res = await fetch(url);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.error?.message || `Failed to fetch channel videos (${res.status})`);
            }
            const items = Array.isArray(data?.items) ? data.items : [];
            const videos: ChannelVideoBrowserItem[] = items
                .filter((item: any) => item?.id?.videoId && item?.snippet)
                .map((item: any) => {
                    const videoId = item.id.videoId;
                    const sn = item.snippet || {};
                    return {
                        videoId,
                        title: sn.title || `Video ${videoId}`,
                        publishedAt: sn.publishedAt || "",
                        thumbnail: sn.thumbnails?.medium?.url || sn.thumbnails?.default?.url || `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
                        url: `https://www.youtube.com/watch?v=${videoId}`,
                    };
                });
            setChannelVideosBySubscription((prev) => ({ ...prev, [sub.id]: videos }));
            toast({ title: "Channel videos fetched", description: `Loaded ${videos.length} video(s) from ${sub.channelName}.` });
        } catch (error: any) {
            toast({ title: "Failed to fetch videos", description: error?.message || "Could not load channel videos", variant: "destructive" });
        } finally {
            setLoadingChannelVideos((prev) => ({ ...prev, [sub.id]: false }));
        }
    };

    const fetchChannelPlaylistsForSubscription = async (sub: YouTubeSubscription) => {
        setLoadingChannelPlaylists((prev) => ({ ...prev, [sub.id]: true }));
        try {
            const url = `${YOUTUBE_DATA_API_BASE}/playlists?part=snippet,contentDetails&channelId=${encodeURIComponent(sub.channelId)}&maxResults=20&key=${encodeURIComponent(YOUTUBE_DATA_API_KEY)}`;
            const res = await fetch(url);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.error?.message || `Failed to fetch channel playlists (${res.status})`);
            }
            const items = Array.isArray(data?.items) ? data.items : [];
            const playlists: ChannelPlaylistBrowserItem[] = items
                .filter((item: any) => item?.id && item?.snippet)
                .map((item: any) => {
                    const sn = item.snippet || {};
                    return {
                        playlistId: item.id,
                        title: sn.title || `Playlist ${item.id}`,
                        publishedAt: sn.publishedAt || "",
                        itemCount: Number(item?.contentDetails?.itemCount || 0),
                        thumbnail: sn.thumbnails?.medium?.url || sn.thumbnails?.default?.url || "",
                        url: `https://www.youtube.com/playlist?list=${item.id}`,
                    };
                });
            setChannelPlaylistsBySubscription((prev) => ({ ...prev, [sub.id]: playlists }));
            toast({ title: "Channel playlists fetched", description: `Loaded ${playlists.length} playlist(s) from ${sub.channelName}.` });
        } catch (error: any) {
            toast({ title: "Failed to fetch playlists", description: error?.message || "Could not load channel playlists", variant: "destructive" });
        } finally {
            setLoadingChannelPlaylists((prev) => ({ ...prev, [sub.id]: false }));
        }
    };

    const fetchTranscriptForChannelVideo = async (subscriptionId: string, video: ChannelVideoBrowserItem) => {
        setChannelVideosBySubscription((prev) => ({
            ...prev,
            [subscriptionId]: (prev[subscriptionId] || []).map((v) =>
                v.videoId === video.videoId ? { ...v, isFetchingTranscript: true } : v
            ),
        }));
        try {
            const res = await fetch(`${TRANSCRIPT_API_ENDPOINT}?video_id=${encodeURIComponent(video.videoId)}`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const message = data?.error || data?.detail || res.statusText || "Failed to fetch transcript";
                throw new Error(message);
            }
            const transcriptArray = data?.transcript || [];
            const transcriptText = Array.isArray(transcriptArray)
                ? transcriptArray.map((item: { text?: string }) => item?.text ?? "").filter(Boolean).join("\n")
                : "";
            setChannelVideosBySubscription((prev) => ({
                ...prev,
                [subscriptionId]: (prev[subscriptionId] || []).map((v) =>
                    v.videoId === video.videoId
                        ? { ...v, transcript: transcriptText || "(No transcript text)", isFetchingTranscript: false }
                        : v
                ),
            }));
            toast({ title: "Transcript fetched", description: `"${video.title || `Video ${video.videoId}`}" is ready.` });
        } catch (error: any) {
            setChannelVideosBySubscription((prev) => ({
                ...prev,
                [subscriptionId]: (prev[subscriptionId] || []).map((v) =>
                    v.videoId === video.videoId ? { ...v, isFetchingTranscript: false } : v
                ),
            }));
            toast({ title: "Failed to fetch transcript", description: error?.message || "Transcript fetch failed", variant: "destructive" });
        }
    };

    const sendChannelVideoToDocumentCreator = (channelName: string, video: ChannelVideoBrowserItem) => {
        if (!video.transcript) {
            toast({
                title: "No transcript available",
                description: "Fetch transcript first, then send it to Document Creator.",
                variant: "destructive"
            });
            return;
        }
        handleSendToDocumentCreator({
            transcript: video.transcript,
            videoId: video.videoId,
            title: video.title,
            channelName
        });
    };

    const openContentBrowser = (sub: YouTubeSubscription, mode: "videos" | "playlists") => {
        setBrowserSubscription(sub);
        setBrowserMode(mode);
        setBrowserSearchQuery("");
        setBrowserOpen(true);
    };

    const handleFetchVideosInPopup = async (sub: YouTubeSubscription) => {
        openContentBrowser(sub, "videos");
        await fetchChannelVideosForSubscription(sub);
    };

    const handleFetchPlaylistsInPopup = async (sub: YouTubeSubscription) => {
        openContentBrowser(sub, "playlists");
        await fetchChannelPlaylistsForSubscription(sub);
    };

    const transcribePlaylistInBrowser = async (sub: YouTubeSubscription, playlist: ChannelPlaylistBrowserItem) => {
        const stateKey = `${sub.id}:${playlist.playlistId}`;
        setPlaylistTranscriptState((prev) => ({
            ...prev,
            [stateKey]: {
                isFetching: true,
                videos: prev[stateKey]?.videos || []
            }
        }));
        try {
            const res = await fetch(`${TRANSCRIPT_API_ENDPOINT}?playlist_id=${encodeURIComponent(playlist.playlistId)}`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.error || data?.detail || res.statusText || "Failed to transcribe playlist");
            }
            const videos = Array.isArray(data?.videos) ? data.videos : [];
            const transcriptVideos = videos
                .map((v: any) => ({
                    videoId: v.videoId || v.video_id || "",
                    title: v.title || "YouTube Video",
                    transcript: Array.isArray(v.transcript)
                        ? v.transcript.map((item: { text?: string }) => item?.text ?? "").filter(Boolean).join("\n")
                        : ""
                }))
                .filter((v: any) => v.videoId && v.transcript);
            setPlaylistTranscriptState((prev) => ({
                ...prev,
                [stateKey]: {
                    isFetching: false,
                    videos: transcriptVideos
                }
            }));
            toast({
                title: "Playlist transcribed",
                description: `${transcriptVideos.length} video transcript(s) fetched from "${playlist.title}".`
            });
        } catch (error: any) {
            setPlaylistTranscriptState((prev) => ({
                ...prev,
                [stateKey]: {
                    isFetching: false,
                    videos: prev[stateKey]?.videos || []
                }
            }));
            toast({
                title: "Playlist transcription failed",
                description: error?.message || "Failed to transcribe playlist",
                variant: "destructive"
            });
        }
    };

    const sendPlaylistToDocumentCreatorFromBrowser = (sub: YouTubeSubscription, playlist: ChannelPlaylistBrowserItem) => {
        const stateKey = `${sub.id}:${playlist.playlistId}`;
        const videos = playlistTranscriptState[stateKey]?.videos || [];
        if (!videos.length) {
            toast({
                title: "No transcripts available",
                description: "Transcribe the playlist first.",
                variant: "destructive"
            });
            return;
        }
        cancelDocumentEdit();
        for (const video of videos) {
            const videoLink = `https://www.youtube.com/watch?v=${video.videoId}`;
            const videoTitle = (video.title && video.title !== "YouTube Video" ? video.title : `Video ${video.videoId}`) || `Video ${video.videoId}`;
            const formattedContent = `<h1>${videoTitle}</h1>
<p><strong>Channel:</strong> ${sub.channelName}</p>
<p><strong>Playlist:</strong> ${playlist.title}</p>
<p><strong>Video URL:</strong> <a href="${videoLink}" target="_blank">${videoLink}</a></p>
<p><strong>Video ID:</strong> ${video.videoId}</p>
<hr/>
<h2>Transcript</h2>
<div>${video.transcript}</div>`;
            clipToSidebar(DOMPurify.sanitize(formattedContent), videoTitle, "youtube");
        }
        toast({
            title: "Sent to Document Creator",
            description: `${videos.length} document(s) created from "${playlist.title}".`
        });
    };

    const handleSubscribe = (channel: ChannelSearchResult) => {
        setSelectedChannel(channel);
        setShowSubscribeDialog(true);
    };

    const confirmSubscribe = () => {
        if (!selectedChannel) return;
        subscribeMutation.mutate({
            channelId: selectedChannel.channelId,
            channelName: selectedChannel.channelName,
            channelUrl: selectedChannel.channelUrl,
            channelThumbnail: selectedChannel.channelThumbnail,
            subscriberCount: selectedChannel.subscriberCount,
            ...subscribeSettings
        });
    };

    // Handler for clipping selected text to sidebar
    const handleClipToSidebar = (content: string) => {
        const sanitizedContent = DOMPurify.sanitize(content);
        clipToSidebar(sanitizedContent);
        toast({
            title: "Clipped to sidebar",
            description: "Selected content has been added to the document editor.",
        });
    };

    // Handler for sending transcript(s) to sidebar. Pass one item to send that video, or no arg to send all.
    const handleSendToDocumentCreator = (item?: { transcript: string; videoId: string; title: string; channelName?: string }) => {
        const toSend = item ? [item] : videoTranscripts;
        if (!toSend.length) {
            toast({
                title: "No transcript available",
                description: "Please fetch transcript(s) first.",
                variant: "destructive"
            });
            return;
        }

        cancelDocumentEdit();
        for (const t of toSend) {
            const videoLink = `https://www.youtube.com/watch?v=${t.videoId}`;
            const videoTitle = (t.title && t.title !== 'YouTube Video' ? t.title : `Video ${t.videoId}`) || `Video ${t.videoId}`;
            const formattedContent = `<h1>${videoTitle}</h1>
${t.channelName ? `<p><strong>Channel:</strong> ${t.channelName}</p>` : ''}
<p><strong>Video URL:</strong> <a href="${videoLink}" target="_blank">${videoLink}</a></p>
<p><strong>Video ID:</strong> ${t.videoId}</p>
<hr/>
<h2>Transcript</h2>
<div>${t.transcript}</div>`;
            const sanitizedContent = DOMPurify.sanitize(formattedContent);
            clipToSidebar(sanitizedContent, videoTitle, "youtube");
        }

        toast({
            title: "Sent to Document Creator",
            description: toSend.length === 1
                ? `"${toSend[0].title}" is ready to edit.`
                : `${toSend.length} documents created — one per video.`,
        });
    };

    // Handler for sending each playlist video transcript to Document Creator (separate document per video)
    const handleSendPlaylistToDocumentCreator = () => {
        if (!playlistTranscripts?.videos?.length) {
            toast({
                title: "No playlist data",
                description: "Please fetch playlist transcripts first.",
                variant: "destructive"
            });
            return;
        }
        const videosWithTranscript = playlistTranscripts.videos.filter(
            (v: any) => v.transcript && Array.isArray(v.transcript) && v.transcript.length > 0
        );
        if (videosWithTranscript.length === 0) {
            toast({
                title: "No transcripts to send",
                description: "None of the playlist videos have transcripts available.",
                variant: "destructive"
            });
            return;
        }
        cancelDocumentEdit();
        const videoLinkBase = "https://www.youtube.com/watch?v=";
        for (const video of videosWithTranscript) {
            const transcriptText = video.transcript.map((item: { text?: string }) => item?.text ?? '').filter(Boolean).join('\n');
            const videoLink = `${videoLinkBase}${video.videoId}`;
            const videoTitle = (video.title && video.title !== 'YouTube Video' ? video.title : `Video ${video.videoId}`) || `Video ${video.videoId}`;
            const formattedContent = `<h1>${videoTitle}</h1>
<p><strong>Video URL:</strong> <a href="${videoLink}" target="_blank">${videoLink}</a></p>
<p><strong>Video ID:</strong> ${video.videoId}</p>
<hr/>
<h2>Transcript</h2>
<div>${transcriptText}</div>`;
            const sanitizedContent = DOMPurify.sanitize(formattedContent);
            clipToSidebar(sanitizedContent, videoTitle, "youtube");
        }
        toast({
            title: "Playlist sent to Document Creator",
            description: `${videosWithTranscript.length} document(s) created — one per video.`,
        });
    };

    const filteredAndSortedSubscriptions = useMemo(() => {
        let filtered = [...subscriptions];
        
        // Apply search filter
        if (subscriptionSearchQuery && subscriptionSearchQuery.trim()) {
            const query = subscriptionSearchQuery.toLowerCase().trim();
            filtered = filtered.filter(sub => 
                sub.channelName.toLowerCase().includes(query) ||
                sub.channelId.toLowerCase().includes(query)
            );
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
            let comparison = 0;
            if (subscriptionSortField === "name") {
                comparison = a.channelName.localeCompare(b.channelName);
            } else if (subscriptionSortField === "lastSynced") {
                const aTime = a.lastSyncedAt ? new Date(a.lastSyncedAt).getTime() : 0;
                const bTime = b.lastSyncedAt ? new Date(b.lastSyncedAt).getTime() : 0;
                comparison = aTime - bTime;
            } else if (subscriptionSortField === "subscribers") {
                const aCount = parseInt(a.subscriberCount?.replace(/,/g, '') || '0');
                const bCount = parseInt(b.subscriberCount?.replace(/,/g, '') || '0');
                comparison = aCount - bCount;
            }
            return subscriptionSortDirection === "asc" ? comparison : -comparison;
        });
        return filtered;
    }, [subscriptions, subscriptionSortField, subscriptionSortDirection, subscriptionSearchQuery]);

    const filteredAndSortedImportJobs = useMemo(() => {
        let filtered = [...importJobs];
        
        // Apply search filter
        if (jobSearchQuery && jobSearchQuery.trim()) {
            const query = jobSearchQuery.toLowerCase().trim();
            filtered = filtered.filter(job => 
                (job.title || '').toLowerCase().includes(query) ||
                (job.externalId || '').toLowerCase().includes(query) ||
                job.status.toLowerCase().includes(query)
            );
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
            let comparison = 0;
            if (jobSortField === "title") {
                const aTitle = a.title || a.externalId || '';
                const bTitle = b.title || b.externalId || '';
                comparison = aTitle.localeCompare(bTitle);
            } else if (jobSortField === "status") {
                comparison = a.status.localeCompare(b.status);
            } else if (jobSortField === "createdAt") {
                comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            }
            return jobSortDirection === "asc" ? comparison : -comparison;
        });
        return filtered;
    }, [importJobs, jobSortField, jobSortDirection, jobSearchQuery]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "completed":
                return <CheckCircle className="h-4 w-4 text-green-500"/>;
            case "failed":
                return <XCircle className="h-4 w-4 text-red-500"/>;
            case "processing":
                return <Loader2 className="h-4 w-4 animate-spin text-blue-500"/>;
            case "pending":
                return <Clock className="h-4 w-4 text-yellow-500"/>;
            default:
                return <AlertCircle className="h-4 w-4 text-muted-foreground"/>;
        }
    };

    return (
        <div className="container mx-auto p-6 max-w-6xl">
            <div className="flex items-center gap-3 mb-6">
                <Youtube className="h-8 w-8 text-red-500"/>
                <div>
                    <h1 className="text-2xl font-bold" data-testid="text-page-title">Youtube Automation</h1>
                    <p className="text-muted-foreground">Automatically import and organize YouTube content</p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "subscriptions" | "import" | "jobs")} className="space-y-4">
                <div className="flex items-center justify-between">
                    <TabsList>
                        <TabsTrigger value="import" data-testid="tab-import">
                            <Plus className="h-4 w-4 mr-2"/>
                            Quick Import
                        </TabsTrigger>
                        <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
                            <Users className="h-4 w-4 mr-2"/>
                            Channels
                        </TabsTrigger>
                        <TabsTrigger value="jobs" data-testid="tab-jobs">
                            <Clock className="h-4 w-4 mr-2"/>
                            Import History
                        </TabsTrigger>
                    </TabsList>
                </div>

                {(activeTab === "subscriptions" || activeTab === "jobs") && (
                    <div className="flex items-center gap-2 flex-wrap mt-4">
                        <div className="relative flex-1 min-w-[200px] max-w-sm">
                            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder={activeTab === "subscriptions" ? "Search channels..." : "Search import history..."}
                                value={activeTab === "subscriptions" ? subscriptionSearchQuery : jobSearchQuery}
                                onChange={(e) => {
                                    if (activeTab === "subscriptions") {
                                        setSubscriptionSearchQuery(e.target.value);
                                    } else {
                                        setJobSearchQuery(e.target.value);
                                    }
                                }}
                                className="pl-8 h-9 text-sm"
                            />
                        </div>

                        {activeTab === "subscriptions" && (
                            <Select 
                                value={`${subscriptionSortField}-${subscriptionSortDirection}`} 
                                onValueChange={(value) => {
                                    const [field, direction] = value.split("-") as ["name" | "lastSynced" | "subscribers", "asc" | "desc"];
                                    if (field && direction) {
                                        setSubscriptionSortField(field);
                                        setSubscriptionSortDirection(direction);
                                    }
                                }}
                            >
                                <SelectTrigger className="w-[160px] h-9 text-sm">
                                    <SelectValue placeholder="Sort by">
                                        {subscriptionSortField === "name" && (subscriptionSortDirection === "asc" ? "Name A → Z" : "Name Z → A")}
                                        {subscriptionSortField === "lastSynced" && (subscriptionSortDirection === "asc" ? "Last Synced ↑" : "Last Synced ↓")}
                                        {subscriptionSortField === "subscribers" && (subscriptionSortDirection === "asc" ? "Subscribers ↑" : "Subscribers ↓")}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="name-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>Name A → Z</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="name-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            <span>Name Z → A</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="lastSynced-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>Last Synced ↑</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="lastSynced-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            <span>Last Synced ↓</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="subscribers-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>Subscribers ↑</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="subscribers-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            <span>Subscribers ↓</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        )}

                        {activeTab === "jobs" && (
                            <Select 
                                value={`${jobSortField}-${jobSortDirection}`} 
                                onValueChange={(value) => {
                                    const [field, direction] = value.split("-") as ["title" | "status" | "createdAt", "asc" | "desc"];
                                    if (field && direction) {
                                        setJobSortField(field);
                                        setJobSortDirection(direction);
                                    }
                                }}
                            >
                                <SelectTrigger className="w-[160px] h-9 text-sm">
                                    <SelectValue placeholder="Sort by">
                                        {jobSortField === "title" && (jobSortDirection === "asc" ? "Title A → Z" : "Title Z → A")}
                                        {jobSortField === "status" && (jobSortDirection === "asc" ? "Status A → Z" : "Status Z → A")}
                                        {jobSortField === "createdAt" && (jobSortDirection === "asc" ? "Date ↑" : "Date ↓")}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="title-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>Title A → Z</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="title-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            <span>Title Z → A</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="status-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>Status A → Z</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="status-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            <span>Status Z → A</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="createdAt-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3" />
                                            <span>Date ↑</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="createdAt-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3" />
                                            <span>Date ↓</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        )}

                        {activeTab === "subscriptions" && (
                            <div className="flex items-center gap-0.5 border rounded-md p-0.5">
                                <Button
                                    variant={subscriptionViewMode === "grid" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setSubscriptionViewMode("grid")}
                                    title="Grid view"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant={subscriptionViewMode === "list" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setSubscriptionViewMode("list")}
                                    title="List view"
                                >
                                    <List className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        )}

                        {activeTab === "jobs" && (
                            <div className="flex items-center gap-0.5 border rounded-md p-0.5">
                                <Button
                                    variant={jobViewMode === "grid" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setJobViewMode("grid")}
                                    title="Grid view"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant={jobViewMode === "list" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setJobViewMode("list")}
                                    title="List view"
                                >
                                    <List className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <TabsContent value="subscriptions" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Search a channel</CardTitle>
                            <CardDescription>Find and subscribe to YouTube channels for automatic video
                                import</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Search for channels..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                    data-testid="input-channel-search"
                                />
                                <Button onClick={handleSearch} disabled={isSearching} data-testid="button-search">
                                    {isSearching ? <Loader2 className="h-4 w-4 animate-spin"/> :
                                        <Search className="h-4 w-4"/>}
                                </Button>
                            </div>

                            {showSearchResults && searchResults.length > 0 && (
                                <Accordion
                                    type="single"
                                    collapsible
                                    value={searchResultsAccordionValue}
                                    onValueChange={(value) => setSearchResultsAccordionValue(value || undefined)}
                                    className="mt-4 w-full rounded-lg border"
                                >
                                    <AccordionItem value="channel-search-results" className="border-b-0">
                                        <div className="flex items-center gap-2 px-3">
                                            <AccordionTrigger className="flex-1 py-3 hover:no-underline">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">Search results</span>
                                                    <Badge variant="secondary">{searchResults.length}</Badge>
                                                </div>
                                            </AccordionTrigger>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleCloseSearchResults}
                                                className="shrink-0"
                                            >
                                                <XCircle className="h-4 w-4 mr-1"/>
                                                Close
                                            </Button>
                                        </div>
                                        <AccordionContent className="px-3 pb-3">
                                            <div className="space-y-2">
                                                {searchResults.map((channel) => (
                                                    <div
                                                        key={channel.channelId}
                                                        className="flex items-center gap-4 p-3 rounded-lg border hover-elevate"
                                                        data-testid={`card-channel-${channel.channelId}`}
                                                    >
                                                        <Avatar className="h-12 w-12">
                                                            <AvatarImage src={channel.channelThumbnail} alt={channel.channelName}/>
                                                            <AvatarFallback>{channel.channelName.substring(0, 2)}</AvatarFallback>
                                                        </Avatar>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium truncate">{channel.channelName}</div>
                                                            <div
                                                                className="text-sm text-muted-foreground">{channel.subscriberCount} subscribers
                                                            </div>
                                                            <div
                                                                className="text-sm text-muted-foreground line-clamp-1">{channel.description}</div>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleSubscribe(channel)}
                                                            data-testid={`button-subscribe-${channel.channelId}`}
                                                        >
                                                            <Plus className="h-4 w-4 mr-1"/>
                                                            Subscribe
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Your Subscriptions</CardTitle>
                                    <CardDescription>Channels you're tracking for automatic video imports and
                                        transcription</CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    {isPolling && (
                                        <Badge variant="outline" className="flex items-center gap-1">
                                            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"/>
                                            Auto-tracking Active
                                        </Badge>
                                    )}
                                    {subscriptions.length > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                                const activeSubs = subscriptions.filter(sub => sub.isActive);
                                                for (const sub of activeSubs) {
                                                    try {
                                                        const data = await youtubeSubscriptionService.syncSubscription(sub.id);
                                                        if (data.newVideos > 0) {
                                                            toast({
                                                                title: "Sync complete",
                                                                description: `${data.newVideos} new video(s) from ${sub.channelName} imported`
                                                            });
                                                        }
                                                    } catch (error: any) {
                                                        console.error(`Failed to sync ${sub.channelName}:`, error);
                                                        toast({
                                                            title: "Sync failed",
                                                            description: `Failed to sync ${sub.channelName}: ${error.message}`,
                                                            variant: "destructive"
                                                        });
                                                    }
                                                }
                                                queryClient.invalidateQueries({queryKey: ["youtube-subscriptions"]});
                                                queryClient.invalidateQueries({queryKey: ["youtube-import-jobs"]});
                                                queryClient.invalidateQueries({queryKey: ["/api/sources"]});
                                            }}
                                        >
                                            <RefreshCw className="h-4 w-4 mr-2"/>
                                            Sync All
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loadingSubscriptions ? (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin"/>
                                </div>
                            ) : subscriptions.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No channel subscriptions yet. Search for channels above to get started.
                                </div>
                            ) : filteredAndSortedSubscriptions.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No subscriptions match your search criteria.
                                </div>
                            ) : (
                                <div className={subscriptionViewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-3"}>
                                    {filteredAndSortedSubscriptions.map((sub) => (
                                        <div
                                            key={sub.id}
                                            className={subscriptionViewMode === "grid" 
                                                ? "flex flex-col gap-3 p-4 rounded-lg border" 
                                                : "flex items-center gap-4 p-4 rounded-lg border"}
                                            data-testid={`card-subscription-${sub.id}`}
                                        >
                                            <div className={subscriptionViewMode === "grid" ? "flex flex-col items-center text-center" : "flex items-center gap-4"}>
                                                <Avatar className={subscriptionViewMode === "grid" ? "h-16 w-16" : "h-12 w-12"}>
                                                    <AvatarImage src={sub.channelThumbnail || ""} alt={sub.channelName}/>
                                                    <AvatarFallback>{sub.channelName.substring(0, 2)}</AvatarFallback>
                                                </Avatar>
                                                <div className={subscriptionViewMode === "grid" ? "flex-1 w-full mt-2" : "flex-1 min-w-0"}>
                                                    <div className={subscriptionViewMode === "grid" ? "flex flex-col items-center gap-2" : "flex items-center gap-2 flex-wrap"}>
                                                        <span className="font-medium">{sub.channelName}</span>
                                                        <div className="flex items-center gap-2 flex-wrap justify-center">
                                                            <Badge variant={sub.isActive ? "default" : "secondary"}>
                                                                {sub.isActive ? "Active" : "Paused"}
                                                            </Badge>
                                                            <Badge variant="outline">{sub.syncFrequency}</Badge>
                                                        </div>
                                                    </div>
                                                    <div className={`text-sm text-muted-foreground ${subscriptionViewMode === "grid" ? "text-center mt-2" : ""}`}>
                                                        {sub.subscriberCount &&
                                                            <span>{sub.subscriberCount} subscribers</span>}
                                                        {sub.lastSyncedAt && (
                                                            <span className={subscriptionViewMode === "grid" ? "block mt-1" : "ml-2"}>
                                  Last synced: {new Date(sub.lastSyncedAt).toLocaleDateString()} {new Date(sub.lastSyncedAt).toLocaleTimeString()}
                                </span>
                                                        )}
                                                        {sub.autoImport && (
                                                            <Badge variant="secondary" className={subscriptionViewMode === "grid" ? "mt-2 text-xs" : "ml-2 text-xs"}>
                                                                Auto-transcribe enabled
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={subscriptionViewMode === "grid" ? "flex items-center justify-center gap-2 mt-2 flex-wrap" : "flex items-center gap-2 flex-wrap"}>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleFetchVideosInPopup(sub)}
                                                    disabled={!!loadingChannelVideos[sub.id]}
                                                >
                                                    {loadingChannelVideos[sub.id] ? (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                                    ) : (
                                                        <Play className="h-4 w-4 mr-2"/>
                                                    )}
                                                    Fetch videos
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleFetchPlaylistsInPopup(sub)}
                                                    disabled={!!loadingChannelPlaylists[sub.id]}
                                                >
                                                    {loadingChannelPlaylists[sub.id] ? (
                                                        <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                                    ) : (
                                                        <List className="h-4 w-4 mr-2"/>
                                                    )}
                                                    Fetch playlists
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    onClick={() => syncMutation.mutate(sub.id)}
                                                    disabled={syncMutation.isPending}
                                                    data-testid={`button-sync-${sub.id}`}
                                                >
                                                    <RefreshCw
                                                        className={`h-4 w-4 ${syncMutation.isPending ? "animate-spin" : ""}`}/>
                                                </Button>
                                                {sub.channelUrl && (
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        asChild
                                                    >
                                                        <a href={sub.channelUrl} target="_blank"
                                                           rel="noopener noreferrer">
                                                            <ExternalLink className="h-4 w-4"/>
                                                        </a>
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    onClick={() => unsubscribeMutation.mutate(sub.id)}
                                                    disabled={unsubscribeMutation.isPending}
                                                    data-testid={`button-unsubscribe-${sub.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4"/>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="import" className="space-y-4">
                    <Tabs value={quickImportSubTab} onValueChange={(v) => setQuickImportSubTab(v as "video" | "playlist")} className="space-y-4">
                        <TabsList className="grid w-full max-w-[240px] grid-cols-2">
                            <TabsTrigger value="video" data-testid="tab-quick-import-video">
                                <Play className="h-4 w-4 mr-2" />
                                Video
                            </TabsTrigger>
                            <TabsTrigger value="playlist" data-testid="tab-quick-import-playlist">
                                <List className="h-4 w-4 mr-2" />
                                Playlist
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="video" className="space-y-4 mt-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Quick Video Import</CardTitle>
                                    <CardDescription>Paste one or more YouTube video URLs (one per line) to transcribe</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex flex-row gap-2 items-start">
                                        <Textarea
                                            placeholder="Paste one or more YouTube URLs, one per line..."
                                            value={videoUrl}
                                            onChange={(e) => setVideoUrl(e.target.value)}
                                            data-testid="input-video-url"
                                            className="min-h-[72px] font-mono text-sm flex-1 min-w-0"
                                            rows={2}
                                        />
                                        <div className="flex flex-col gap-2 flex-shrink-0">
                                            <Button
                                                onClick={fetchVideoTranscript}
                                                disabled={!videoUrl.trim() || isFetchingVideo}
                                                data-testid="button-fetch-transcript"
                                            >
                                                {isFetchingVideo ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                                ) : (
                                                    <Play className="h-4 w-4 mr-2"/>
                                                )}
                                                Fetch Transcript
                                            </Button>
                                            {videoTranscripts.length > 1 && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleSendToDocumentCreator()}
                                                    className="flex items-center gap-1.5"
                                                    title="Create one document per video"
                                                    data-testid="button-send-all-to-document-creator"
                                                >
                                                    <FileText className="h-3.5 w-3.5" />
                                                    Send all to Document Creator
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        One URL per line. Transcripts are fetched and shown below; send individually or all to Document Creator.
                                    </p>

                                    {videoDuplicatesSkipped.length > 0 && (
                                        <div className="mt-4 rounded-lg border border-amber-500/50 bg-amber-500/5 p-3">
                                            <p className="text-sm font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                                Skipped (duplicate) — not fetched: {videoDuplicatesSkipped.length} video(s)
                                            </p>
                                            <ul className="mt-2 text-sm text-muted-foreground space-y-1 list-disc list-inside">
                                                {videoDuplicatesSkipped.map((d) => (
                                                    <li key={d.videoId}>
                                                        <span className="font-medium text-foreground">{d.title}</span>
                                                        {d.reason === "url" ? " — URL already in library or repeated in this list" : " — Title already in library or repeated in this list"}
                                                        <span className="text-xs ml-1">({d.videoId})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {videoTranscripts.length > 0 && (
                                        <Accordion type="single" collapsible defaultValue={videoTranscripts[0].videoId} className="mt-4 w-full rounded-lg border bg-muted/50">
                                            {videoTranscripts.map((vt) => (
                                                <AccordionItem key={vt.videoId} value={vt.videoId} className="border-b last:border-b-0 group">
                                                    <div className="flex items-center gap-2 px-4 py-3 group-data-[state=open]:bg-red-500/5">
                                                        <AccordionTrigger className="flex-1 hover:no-underline [&[data-state=open]]:bg-transparent px-0 py-0 h-auto">
                                                            <div className="flex items-center gap-3 text-left">
                                                                {vt.thumbnailUrl && (
                                                                    <img
                                                                        src={vt.thumbnailUrl}
                                                                        alt=""
                                                                        className="w-20 h-12 object-cover rounded flex-shrink-0"
                                                                    />
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <span className="font-semibold line-clamp-1">{vt.title || `Video ${vt.videoId}`}</span>
                                                                    {vt.channelName && (
                                                                        <p className="text-xs text-muted-foreground truncate">{vt.channelName}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </AccordionTrigger>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleSendToDocumentCreator(vt)}
                                                            className="flex items-center gap-1.5 flex-shrink-0"
                                                            title="Send this transcript to Document Creator"
                                                            data-testid="button-send-to-document-creator"
                                                        >
                                                            <FileText className="h-3.5 w-3.5" />
                                                            Send to Document Creator
                                                        </Button>
                                                    </div>
                                                    <AccordionContent className="px-4">
                                                        <div className="rounded-md bg-background/80 border p-3">
                                                            <p className="text-xs text-muted-foreground mb-2">Transcript · Video ID: {vt.videoId}</p>
                                                            <div className="max-h-96 overflow-y-auto select-text cursor-text text-sm whitespace-pre-wrap leading-relaxed" data-message-content>
                                                                {vt.transcript}
                                                            </div>
                                                        </div>
                                                    </AccordionContent>
                                                </AccordionItem>
                                            ))}
                                        </Accordion>
                                    )}
                        </CardContent>
                    </Card>
                        </TabsContent>

                        <TabsContent value="playlist" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Playlist Import</CardTitle>
                            <CardDescription>Import and transcribe all videos from a YouTube playlist</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-row gap-2 items-start">
                                <Input
                                    placeholder="https://youtube.com/playlist?list=..."
                                    value={playlistUrl}
                                    onChange={(e) => setPlaylistUrl(e.target.value)}
                                    data-testid="input-playlist-url"
                                    className="flex-1 min-w-0"
                                />
                                <Button
                                    onClick={fetchPlaylistTranscripts}
                                    disabled={!playlistUrl || isFetchingPlaylist}
                                    data-testid="button-import-playlist"
                                    className="flex-shrink-0"
                                >
                                    {isFetchingPlaylist ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                    ) : (
                                        <List className="h-4 w-4 mr-2"/>
                                    )}
                                    Import Playlist
                                </Button>
                            </div>
                            {playlistProgress && (
                                <div className="mt-4 space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">
                                            Processing video {playlistProgress.current} of {playlistProgress.total}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {Math.round((playlistProgress.current / playlistProgress.total) * 100)}%
                                        </span>
                                    </div>
                                    <Progress
                                        value={(playlistProgress.current / playlistProgress.total) * 100}
                                        className="h-2"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Video ID: {playlistProgress.videoId}
                                    </p>
                                </div>
                            )}
                            <p className="text-sm text-muted-foreground mt-2">
                                All videos in the playlist will be imported and transcribed. Send to Document Creator creates one document per video.
                            </p>

                            {playlistTranscripts && playlistTranscripts.videos.length > 0 && (
                                <div className="mt-4">
                                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                        <p className="text-sm text-muted-foreground">Playlist ID: {playlistTranscripts.playlistId}</p>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="secondary">
                                                {playlistTranscripts.videos.filter((v: any) => v.transcript && v.transcript.length > 0).length} / {playlistTranscripts.total} videos
                                            </Badge>
                                            {playlistTranscripts.videos.some((v: any) => v.duplicate) && (
                                                <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-400">
                                                    {playlistTranscripts.videos.filter((v: any) => v.duplicate).length} duplicate(s) — already in library
                                                </Badge>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleSendPlaylistToDocumentCreator}
                                                disabled={!playlistTranscripts.videos.some((v: any) => v.transcript?.length > 0)}
                                                className="flex items-center gap-1.5"
                                                title="Create a separate document for each video with a transcript"
                                                data-testid="button-send-playlist-to-document-creator"
                                            >
                                                <FileText className="h-3.5 w-3.5" />
                                                Send all to Document Creator
                                            </Button>
                                        </div>
                                    </div>
                                    <Accordion type="single" collapsible defaultValue={playlistTranscripts.videos[0]?.videoId} className="w-full rounded-lg border bg-muted/50">
                                        {playlistTranscripts.videos.map((video: any) => {
                                            const hasTranscript = video.transcript && video.transcript.length > 0;
                                            const transcriptText = hasTranscript
                                                ? video.transcript.map((item: { text?: string }) => item?.text ?? '').filter(Boolean).join('\n')
                                                : '';
                                            return (
                                                <AccordionItem key={video.videoId} value={video.videoId} className="border-b last:border-b-0 group">
                                                    <div className="flex items-center gap-2 px-4 py-3 group-data-[state=open]:bg-red-500/5">
                                                        <AccordionTrigger className="flex-1 hover:no-underline [&[data-state=open]]:bg-transparent px-0 py-0 h-auto">
                                                            <div className="flex items-center gap-3 text-left">
                                                                {video.thumbnail && (
                                                                    <img src={video.thumbnail} alt="" className="w-20 h-12 object-cover rounded flex-shrink-0" />
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <span className="font-semibold line-clamp-1">{video.title || `Video ${video.videoId}`}</span>
                                                                    <p className="text-xs text-muted-foreground truncate">Video ID: {video.videoId}</p>
                                                                </div>
                                                                {video.duplicate && (
                                                                    <Badge variant="outline" className="text-xs flex-shrink-0 border-amber-500/50 text-amber-700 dark:text-amber-400" title={video.duplicateReason === 'url' ? 'URL already in library' : 'Title already in library'}>
                                                                        Duplicate
                                                                    </Badge>
                                                                )}
                                                                {hasTranscript ? (
                                                                    <Badge variant="default" className="text-xs flex-shrink-0">{video.transcript.length} items</Badge>
                                                                ) : (
                                                                    <Badge variant="destructive" className="text-xs flex-shrink-0">{video.transcriptError || 'No transcript'}</Badge>
                                                                )}
                                                            </div>
                                                        </AccordionTrigger>
                                                        {hasTranscript && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    cancelDocumentEdit();
                                                                    const displayTitle = (video.title && video.title !== 'YouTube Video' ? video.title : `Video ${video.videoId}`) || `Video ${video.videoId}`;
                                                                    const formattedContent = `<h1>${displayTitle}</h1>
<p><strong>Video URL:</strong> <a href="https://www.youtube.com/watch?v=${video.videoId}" target="_blank">https://www.youtube.com/watch?v=${video.videoId}</a></p>
<p><strong>Video ID:</strong> ${video.videoId}</p><hr/><h2>Transcript</h2><div>${transcriptText}</div>`;
                                                                    clipToSidebar(DOMPurify.sanitize(formattedContent), displayTitle, "youtube");
                                                                    toast({ title: "Sent to Document Creator", description: `"${displayTitle}" is ready to edit.` });
                                                                }}
                                                                className="flex items-center gap-1.5 flex-shrink-0"
                                                                title="Send this transcript to Document Creator"
                                                            >
                                                                <FileText className="h-3.5 w-3.5" />
                                                                Send to Document Creator
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <AccordionContent className="px-4">
                                                        <div className="rounded-md bg-background/80 border p-3">
                                                            <p className="text-xs text-muted-foreground mb-2">Transcript · Video ID: {video.videoId}</p>
                                                            {hasTranscript ? (
                                                                <div className="max-h-96 overflow-y-auto select-text cursor-text text-sm whitespace-pre-wrap leading-relaxed" data-message-content>
                                                                    {transcriptText}
                                                                </div>
                                                            ) : (
                                                                <p className="text-sm text-muted-foreground">{video.transcriptError || 'No transcript available.'}</p>
                                                            )}
                                                        </div>
                                                    </AccordionContent>
                                                </AccordionItem>
                                            );
                                        })}
                                    </Accordion>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                        </TabsContent>
                    </Tabs>
                </TabsContent>

                <TabsContent value="jobs" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Transcribed YouTube videos</CardTitle>
                            <CardDescription>Videos that have been transcribed and added to your Docs (type = youtube)</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {filteredAndSortedYouTubeDocs.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    {youtubeDocs.length === 0
                                        ? "No transcribed YouTube videos yet. Use Quick Import then Send to Document Creator and save as type youtube."
                                        : "No transcribed videos match your search."}
                                </div>
                            ) : (
                                <div className={jobViewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-2"}>
                                    {filteredAndSortedYouTubeDocs.map((doc) => {
                                        const videoIdFromUrl = doc.videoUrl ? youtubeService.extractVideoId(doc.videoUrl) : null;
                                        const videoIdFromContent = extractVideoIdFromContent(doc.content);
                                        const videoId = videoIdFromUrl ?? videoIdFromContent;
                                        const videoUrl = doc.videoUrl || (videoId ? canonicalYouTubeUrl(videoId) : null);
                                        const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
                                        return (
                                            <div
                                                key={doc.id}
                                                className={jobViewMode === "grid"
                                                    ? "flex flex-col gap-3 p-4 rounded-lg border overflow-hidden"
                                                    : "flex items-center gap-4 p-3 rounded-lg border"}
                                                data-testid={`card-transcribed-video-${doc.id}`}
                                            >
                                                <div className={jobViewMode === "grid" ? "flex flex-col gap-2" : "flex items-center gap-4 flex-1 min-w-0"}>
                                                    {thumbnailUrl && videoUrl && (
                                                        <a
                                                            href={videoUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={jobViewMode === "grid" ? "block w-full aspect-video rounded-md overflow-hidden bg-muted flex-shrink-0" : "flex-shrink-0 w-24 aspect-video rounded overflow-hidden bg-muted"}
                                                        >
                                                            <img
                                                                src={thumbnailUrl}
                                                                alt=""
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </a>
                                                    )}
                                                    <div className="flex flex-col gap-1 min-w-0">
                                                        <span className="font-medium truncate">{doc.title || "Untitled"}</span>
                                                        <div className="text-sm text-muted-foreground">
                                                            {doc.createdAt ? new Date(doc.createdAt).toLocaleString() : "—"}
                                                        </div>
                                                        {videoUrl ? (
                                                            <a
                                                                href={videoUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-sm text-primary hover:underline inline-flex items-center gap-1 w-fit mt-1"
                                                            >
                                                                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                                                                Watch on YouTube
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>Import jobs</CardTitle>
                            <CardDescription>Recent channel/bulk import jobs and their status</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loadingJobs ? (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin"/>
                                </div>
                            ) : importJobs.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No import jobs yet. Sync a channel or run an import to see jobs here.
                                </div>
                            ) : filteredAndSortedImportJobs.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No import jobs match your search criteria.
                                </div>
                            ) : (
                                <div className={jobViewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-2"}>
                                    {filteredAndSortedImportJobs.map((job) => (
                                        <div
                                            key={job.id}
                                            className={jobViewMode === "grid" 
                                                ? "flex flex-col gap-3 p-4 rounded-lg border" 
                                                : "flex items-center gap-4 p-3 rounded-lg border"}
                                            data-testid={`card-job-${job.id}`}
                                        >
                                            <div className={jobViewMode === "grid" ? "flex items-center gap-3" : "flex items-center gap-4"}>
                                                {getStatusIcon(job.status)}
                                                <div className={jobViewMode === "grid" ? "flex-1" : "flex-1 min-w-0"}>
                                                    <div className={jobViewMode === "grid" ? "flex flex-col gap-2" : "flex items-center gap-2"}>
                                                        <span className={jobViewMode === "grid" ? "font-medium" : "font-medium truncate"}>
                                                            {job.title || job.externalId}
                                                        </span>
                                                        {job.sourceType === "subscription" && (
                                                            <Badge variant="secondary" className="text-xs w-fit">
                                                                Auto-import
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className={`text-sm text-muted-foreground ${jobViewMode === "grid" ? "mt-1" : ""}`}>
                                                        {job.progressMessage || job.status}
                                                        {job.errorMessage && (
                                                            <span className="text-red-500 ml-2">{job.errorMessage}</span>
                                                        )}
                                                    </div>
                                                    {job.status === "processing" && job.progress && (
                                                        <Progress value={parseInt(job.progress)} className="mt-1 h-1"/>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`text-sm text-muted-foreground ${jobViewMode === "grid" ? "text-center border-t pt-2" : "text-right"}`}>
                                                <div>{new Date(job.createdAt).toLocaleString()}</div>
                                                {job.completedAt && job.status === "completed" && (
                                                    <div className="text-xs">
                                                        Completed: {new Date(job.completedAt).toLocaleTimeString()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={browserOpen} onOpenChange={setBrowserOpen}>
                <DialogContent className="w-[95vw] max-w-6xl h-[85vh] max-h-[85vh] p-0 flex flex-col">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <DialogTitle className="text-xl">
                                    {browserMode === "videos" ? "Channel Videos" : "Channel Playlists"}
                                </DialogTitle>
                                <DialogDescription>
                                    {browserSubscription
                                        ? `${browserSubscription.channelName} · ${browserMode === "videos" ? "videos" : "playlists"} browser`
                                        : "Browse channel content"}
                                </DialogDescription>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => setBrowserOpen(false)}>
                                <XCircle className="h-4 w-4 mr-1"/>
                                Close
                            </Button>
                        </div>
                    </DialogHeader>

                    <div className="px-6 py-4 border-b space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative flex-1 min-w-[220px]">
                                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                <Input
                                    placeholder={browserMode === "videos" ? "Search videos..." : "Search playlists..."}
                                    value={browserSearchQuery}
                                    onChange={(e) => setBrowserSearchQuery(e.target.value)}
                                    className="pl-8 h-9 text-sm"
                                />
                            </div>
                            <Select
                                value={`name-${browserSortDirection}`}
                                onValueChange={(value) => {
                                    const [, direction] = value.split("-") as ["name", "asc" | "desc"];
                                    setBrowserSortDirection(direction);
                                }}
                            >
                                <SelectTrigger className="w-[160px] h-9 text-sm">
                                    <SelectValue>
                                        {browserSortDirection === "asc" ? "Name A → Z" : "Name Z → A"}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="name-asc">
                                        <div className="flex items-center gap-2">
                                            <ArrowUp className="h-3 w-3"/>
                                            <span>Name A → Z</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="name-desc">
                                        <div className="flex items-center gap-2">
                                            <ArrowDown className="h-3 w-3"/>
                                            <span>Name Z → A</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-0.5 border rounded-md p-0.5">
                                <Button
                                    variant={browserViewMode === "grid" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setBrowserViewMode("grid")}
                                    title="Grid view"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5"/>
                                </Button>
                                <Button
                                    variant={browserViewMode === "list" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setBrowserViewMode("list")}
                                    title="List view"
                                >
                                    <List className="h-3.5 w-3.5"/>
                                </Button>
                            </div>
                        </div>
                        {browserMode === "playlists" && browserPlaylists.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-muted-foreground">Playlists:</span>
                                {browserPlaylists.map((playlist) => (
                                    <Badge key={playlist.playlistId} variant="outline" className="max-w-[220px] truncate">
                                        {playlist.title}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
                        {browserMode === "videos" ? (
                            loadingChannelVideos[browserSubscription?.id || ""] ? (
                                <div className="flex items-center justify-center py-16">
                                    <Loader2 className="h-6 w-6 animate-spin"/>
                                </div>
                            ) : browserVideos.length === 0 ? (
                                <div className="text-center py-16 text-muted-foreground">No videos found.</div>
                            ) : (
                                <div className={browserViewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 gap-3" : "space-y-3"}>
                                    {browserVideos.map((video) => {
                                        const alreadyTranscribed = transcribedVideoIds.has(video.videoId);
                                        return (
                                            <div key={video.videoId} className="rounded-lg border bg-background p-3">
                                                <div className="flex items-start gap-3">
                                                    {video.thumbnail && (
                                                        <img src={video.thumbnail} alt="" className="w-24 h-14 rounded object-cover flex-shrink-0"/>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium line-clamp-1">{video.title || `Video ${video.videoId}`}</p>
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {video.publishedAt ? new Date(video.publishedAt).toLocaleString() : "Unknown publish date"}
                                                        </p>
                                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                            <Badge variant={alreadyTranscribed ? "default" : "secondary"}>
                                                                {alreadyTranscribed ? "Already transcribed" : "Not transcribed"}
                                                            </Badge>
                                                            {video.transcript && <Badge variant="outline">Transcript ready</Badge>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => browserSubscription && fetchTranscriptForChannelVideo(browserSubscription.id, video)}
                                                        disabled={!!video.isFetchingTranscript}
                                                    >
                                                        {video.isFetchingTranscript ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5"/>
                                                        ) : (
                                                            <Play className="h-3.5 w-3.5 mr-1.5"/>
                                                        )}
                                                        Fetch transcript
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => browserSubscription && sendChannelVideoToDocumentCreator(browserSubscription.channelName, video)}
                                                        disabled={!video.transcript}
                                                    >
                                                        <FileText className="h-3.5 w-3.5 mr-1.5"/>
                                                        Send to Document Creator
                                                    </Button>
                                                    <Button variant="outline" size="icon" asChild>
                                                        <a href={video.url} target="_blank" rel="noopener noreferrer">
                                                            <ExternalLink className="h-4 w-4"/>
                                                        </a>
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        ) : (
                            loadingChannelPlaylists[browserSubscription?.id || ""] ? (
                                <div className="flex items-center justify-center py-16">
                                    <Loader2 className="h-6 w-6 animate-spin"/>
                                </div>
                            ) : browserPlaylists.length === 0 ? (
                                <div className="text-center py-16 text-muted-foreground">No playlists found.</div>
                            ) : (
                                <div className={browserViewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 gap-3" : "space-y-3"}>
                                    {browserPlaylists.map((playlist) => {
                                        const stateKey = browserSubscription ? `${browserSubscription.id}:${playlist.playlistId}` : "";
                                        const pState = playlistTranscriptState[stateKey];
                                        const transcriptCount = pState?.videos?.length || 0;
                                        return (
                                            <div key={playlist.playlistId} className="rounded-lg border bg-background p-3">
                                                <div className="flex items-start gap-3">
                                                    {playlist.thumbnail && (
                                                        <img src={playlist.thumbnail} alt="" className="w-24 h-14 rounded object-cover flex-shrink-0"/>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium line-clamp-1">{playlist.title}</p>
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {playlist.itemCount} videos
                                                            {playlist.publishedAt ? ` · ${new Date(playlist.publishedAt).toLocaleDateString()}` : ""}
                                                        </p>
                                                        {transcriptCount > 0 && (
                                                            <Badge variant="outline" className="mt-2">
                                                                {transcriptCount} transcript(s) ready
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => browserSubscription && transcribePlaylistInBrowser(browserSubscription, playlist)}
                                                        disabled={!!pState?.isFetching}
                                                    >
                                                        {pState?.isFetching ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5"/>
                                                        ) : (
                                                            <Play className="h-3.5 w-3.5 mr-1.5"/>
                                                        )}
                                                        Transcribe playlist
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => browserSubscription && sendPlaylistToDocumentCreatorFromBrowser(browserSubscription, playlist)}
                                                        disabled={transcriptCount === 0}
                                                    >
                                                        <FileText className="h-3.5 w-3.5 mr-1.5"/>
                                                        Send to Document Creator
                                                    </Button>
                                                    <Button variant="outline" size="icon" asChild>
                                                        <a href={playlist.url} target="_blank" rel="noopener noreferrer">
                                                            <ExternalLink className="h-4 w-4"/>
                                                        </a>
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Subscribe to Channel</DialogTitle>
                        <DialogDescription>Configure how you want to track this channel</DialogDescription>
                    </DialogHeader>
                    {selectedChannel && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Avatar>
                                    <AvatarImage src={selectedChannel.channelThumbnail}
                                                 alt={selectedChannel.channelName}/>
                                    <AvatarFallback>{selectedChannel.channelName.substring(0, 2)}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="font-medium">{selectedChannel.channelName}</div>
                                    <div
                                        className="text-sm text-muted-foreground">{selectedChannel.subscriberCount} subscribers
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium">Sync Frequency</div>
                                        <div className="text-sm text-muted-foreground">How often to check for new
                                            videos
                                        </div>
                                    </div>
                                    <Select
                                        value={subscribeSettings.syncFrequency}
                                        onValueChange={(v) => setSubscribeSettings(s => ({...s, syncFrequency: v}))}
                                    >
                                        <SelectTrigger className="w-32" data-testid="select-sync-frequency">
                                            <SelectValue/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="hourly">Hourly</SelectItem>
                                            <SelectItem value="daily">Daily</SelectItem>
                                            <SelectItem value="weekly">Weekly</SelectItem>
                                            <SelectItem value="manual">Manual</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium">Auto Import & Transcribe</div>
                                        <div className="text-sm text-muted-foreground">Automatically import and
                                            transcribe new videos when detected
                                        </div>
                                    </div>
                                    <Switch
                                        checked={subscribeSettings.autoImport}
                                        onCheckedChange={(v) => setSubscribeSettings(s => ({...s, autoImport: v}))}
                                        data-testid="switch-auto-import"
                                    />
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium">AI Enhancement</div>
                                        <div className="text-sm text-muted-foreground">Generate summaries and tags with
                                            AI from transcripts
                                        </div>
                                    </div>
                                    <Switch
                                        checked={subscribeSettings.aiEnhance}
                                        onCheckedChange={(v) => setSubscribeSettings(s => ({...s, aiEnhance: v}))}
                                        data-testid="switch-ai-enhance"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSubscribeDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={confirmSubscribe} disabled={subscribeMutation.isPending}>
                            {subscribeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2"/>}
                            Subscribe
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Selection Toolbar - appears on text selection in transcript */}
            <SelectionToolbar
                onClipToSidebar={handleClipToSidebar}
            />
        </div>
    );
}
